// Four tiers + two non-tier actions
// Tier enum values match DB schema
export type Tier =
  | 'contact_immediately'
  | 'high_trust'
  | 'professional_network'
  | 'loose_connection';
export type GradeAction = Tier | 'not_sure' | 'remove';

export interface TierMeta {
  value: Tier;
  label: string; // Plain English, people-focused
  shortLabel: string; // For compact UI
  colorToken: string; // Mantine color name
  colorShade: number; // Mantine shade index
  hexColor: string; // For direct CSS use
  gradient: string; // CSS gradient for StrataBar fill
  fillPct: number; // Representative fill height (0–100) for StrataBar
  description: string; // One-line explanation, sales register
  personalDescription: string; // Same, in personal-CRM register - names the rhythm, never the opportunity
  sortOrder: number; // 1 = highest priority
  icon: string; // Tabler icon name (as string - import in consumer)
  deferralDays: number; // How long a deferred contact stays out of the outreach queue
  cadenceDays: number; // Keep-in-touch cadence: days between reconnects for this tier
}

export const TIER_MAP: Record<Tier, TierMeta> = {
  contact_immediately: {
    value: 'contact_immediately',
    label: 'Inner Circle',
    shortLabel: 'Inner Circle',
    colorToken: 'green',
    colorShade: 6,
    hexColor: '#2f9e44',
    gradient: 'linear-gradient(to top, #6b4f18 0%, #c4983a 45%, #e8b84b 100%)',
    fillPct: 90,
    description: 'Top priority - exceptional relationship or high-potential, reach out now',
    personalDescription: 'The people closest to you - a check-in about once a month',
    sortOrder: 1,
    icon: 'IconBolt',
    deferralDays: 14,
    cadenceDays: 30,
  },
  high_trust: {
    value: 'high_trust',
    label: 'High Trust',
    shortLabel: 'High Trust',
    colorToken: 'yellow',
    colorShade: 5,
    hexColor: '#f59f00',
    gradient: 'linear-gradient(to top, #3d2d0e 0%, #6b4f18 35%, #c4983a 100%)',
    fillPct: 72,
    description: 'Strong relationship, has seen your work or delivery firsthand',
    personalDescription: 'People you know well and want to keep close - every couple of months',
    sortOrder: 2,
    icon: 'IconStar',
    deferralDays: 30,
    cadenceDays: 60,
  },
  professional_network: {
    value: 'professional_network',
    label: 'Network',
    shortLabel: 'Network',
    colorToken: 'blue',
    colorShade: 5,
    hexColor: '#1c7ed6',
    gradient: 'linear-gradient(to top, #221e14 0%, #3d2d0e 40%, #9a7426 100%)',
    fillPct: 50,
    description: 'Prior colleagues or clients, less closely collaborated',
    personalDescription: 'People you have worked or spent real time with - a few times a year',
    sortOrder: 3,
    icon: 'IconUsers',
    deferralDays: 90,
    cadenceDays: 120,
  },
  loose_connection: {
    value: 'loose_connection',
    label: 'Acquaintance',
    shortLabel: 'Acquaintance',
    colorToken: 'gray',
    colorShade: 5,
    hexColor: '#868e96',
    gradient: 'linear-gradient(to top, #14110a 0%, #2a2518 100%)',
    fillPct: 25,
    description: 'Weak or distant tie - met briefly, peripheral contact',
    personalDescription: 'Met briefly, or drifted a long way - a note once or twice a year',
    sortOrder: 4,
    icon: 'IconUserPlus',
    deferralDays: 180,
    cadenceDays: 240,
  },
};

export const TIERS_BY_PRIORITY = Object.values(TIER_MAP).sort((a, b) => a.sortOrder - b.sortOrder);

/**
 * Keep-in-touch cadence (days between reconnects) for a contact. Tier-derived by
 * default; an optional per-contact override wins when present (Phase 2+).
 */
export function getCadenceDays(tier: Tier, override?: number | null): number {
  return override ?? TIER_MAP[tier].cadenceDays;
}
